import {useEffect, useRef, useCallback, useState} from 'react'

interface UseScrollRestorationOptions<T = unknown> {
  key?: string
  enabled?: boolean
  linkSelector?: string
  variants?: T[]
}

export function useScrollRestoration<T = unknown>(
  options: UseScrollRestorationOptions<T> = {},
) {
  const {
    key = 'productListScrollPosition',
    enabled = true,
    linkSelector = 'a[href*="/vorur/"]',
    variants,
  } = options

  const [router, setRouter] = useState<any>(null)
  const hasRestoredRef = useRef(false)
  const isInitialLoadRef = useRef(true)
  const pendingRestoreRef = useRef(false)

  // Dynamically import useRouter only on client side
  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('next/router').then(({useRouter}) => {
        // We need to get the router instance, not the hook
        import('next/router').then(({default: Router}) => {
          setRouter(Router)
        })
      })
    }
  }, [])

  const saveScrollPosition = useCallback(() => {
    if (!enabled || typeof window === 'undefined') return
    sessionStorage.setItem(key, window.scrollY.toString())
  }, [enabled, key])

  const restoreScrollPosition = useCallback(() => {
    if (!enabled || hasRestoredRef.current || typeof window === 'undefined')
      return

    const savedPosition = sessionStorage.getItem(key)
    if (savedPosition) {
      const targetPosition = parseInt(savedPosition)

      window.scrollTo({
        top: targetPosition,
        behavior: 'auto',
      })

      sessionStorage.removeItem(key)
      hasRestoredRef.current = true
      pendingRestoreRef.current = false
    }
  }, [enabled, key])

  // Save scroll position when navigating to product pages
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const link = target.closest(linkSelector)
      if (link) {
        saveScrollPosition()
        hasRestoredRef.current = false
      }
    }

    document.addEventListener('click', handleClick)

    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [enabled, linkSelector, saveScrollPosition])

  // Check if we should restore on initial load
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    if (isInitialLoadRef.current && sessionStorage.getItem(key)) {
      pendingRestoreRef.current = true
      isInitialLoadRef.current = false

      // If we don't have variants (SSR pages), start attempting restoration immediately
      if (!variants) {
        const attemptRestore = () => {
          const savedPosition = sessionStorage.getItem(key)
          if (!savedPosition) return

          const targetPosition = parseInt(savedPosition)
          const currentHeight = document.documentElement.scrollHeight

          // Check if we have enough content to scroll to the target position
          if (currentHeight > targetPosition + window.innerHeight * 0.5) {
            restoreScrollPosition()
          } else {
            // Retry after a short delay if content isn't ready
            setTimeout(attemptRestore, 100)
          }
        }

        // Start attempting restoration after initial render
        setTimeout(attemptRestore, 50)
      }
    }
  }, [enabled, key, variants, restoreScrollPosition])

  // Restore when variants are available (content is ready)
  useEffect(() => {
    if (pendingRestoreRef.current && variants && variants.length > 0) {
      // Use requestAnimationFrame to ensure DOM is painted
      requestAnimationFrame(() => {
        restoreScrollPosition()
      })
    }
  }, [variants, restoreScrollPosition])

  // Reset flags on route changes (only when router is available)
  useEffect(() => {
    if (!router) return

    const handleRouteChange = () => {
      hasRestoredRef.current = false
      isInitialLoadRef.current = true
      pendingRestoreRef.current = false
    }

    router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router])

  return {
    saveScrollPosition,
    restoreScrollPosition,
  }
}
